import { useNextSanityImage } from 'next-sanity-image'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { P, match } from 'ts-pattern'

import { runQuery, sanityClientLike } from '@/cms/client'
import { imageAssetQuery, imageSchema } from '@/cms/image'
import { useWindowWidth } from '@/hooks/useWindowWidth'
import { getLocaleParts } from '@/utils/locale'
import type { PortableTextTypeComponentProps } from '@portabletext/react'
import { useQuery } from '@tanstack/react-query'

const MOBILE_BREAKPOINT = 550

export const ImageRenderer = (props: {
  node: PortableTextTypeComponentProps<unknown>
  containerWidth: number
  hasPriority: boolean
}) => {
  const { locale } = useRouter()
  const [language, market] = getLocaleParts(locale)
  const windowWidth = useWindowWidth()
  const sanityImage = imageSchema.safeParse(props.node.value)

  const assetRef = match(sanityImage)
    .with(
      { success: true, data: { asset: { _type: 'reference', _ref: P.string } } },
      (si) => si.data.asset._ref
    )
    .otherwise(() => '')

  const { data: imageAsset } = useQuery({
    queryKey: ['imageAsset', language, market, assetRef],
    queryFn: async () => {
      if (assetRef) {
        const asset = await runQuery(imageAssetQuery, { id: assetRef })
        return asset
      }
    },
    enabled: sanityImage.success,
  })

  const imageProps = useNextSanityImage(
    sanityClientLike,
    sanityImage.success ? sanityImage.data : null,
    {
      imageBuilder: (imageUrlBuilder, options) => {
        if (!sanityImage.success) {
          return imageUrlBuilder
        }

        const aspectRatio =
          windowWidth <= MOBILE_BREAKPOINT
            ? sanityImage.data.aspectRatio?.mobile ?? options.croppedImageDimensions.aspectRatio
            : sanityImage.data.aspectRatio?.desktop ?? options.croppedImageDimensions.aspectRatio

        return imageUrlBuilder
          .withOptions({
            w: Math.floor(props.containerWidth),
            h: Math.floor(props.containerWidth / aspectRatio),
            quality: 100,
            focalPoint:
              sanityImage.success && sanityImage.data.hotspot
                ? {
                    x: sanityImage.data.hotspot?.x,
                    y: sanityImage.data.hotspot?.y,
                  }
                : undefined,
          })
          .crop('focalpoint')
          .fit('crop')
      },
    }
  )

  if (!sanityImage.success) {
    return null
  }

  if (!imageProps) {
    return null
  }

  const placeholderProps = match(imageAsset)
    .with(
      {
        metadata: P.not(P.nullish),
      },
      (asset) => ({
        blurDataURL: asset.metadata.lqip,
        placeholder: 'blur',
      })
    )
    .otherwise(() => ({}))

  return (
    <Image
      {...imageProps}
      className="max-w-full rounded-lg"
      alt={sanityImage.data.alt ?? ''}
      priority={props.hasPriority}
      loading={props.hasPriority ? 'eager' : 'lazy'}
      {...placeholderProps}
    />
  )
}
